import { useEffect, useState, useCallback } from "react";

/**
 * Generic spotlight-style guided tour. Give it a list of steps, each
 * pointing at an existing data-testid in the page; it highlights that
 * element and shows a tooltip card with Next/Back/Skip/Finish.
 */
export default function DashboardTour({ steps, onDone }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    const step = steps[i];
    if (!step) return;
    const el = document.querySelector(`[data-testid="${step.targetTestId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => setRect(el.getBoundingClientRect()), 260);
    } else {
      setRect(null);
    }
  }, [i, steps]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  if (!steps.length) return null;
  const step = steps[i];
  const isLast = i === steps.length - 1;
  const tooltipTop = rect ? Math.min(rect.bottom + 16, window.innerHeight - 220) : window.innerHeight / 2 - 90;
  const tooltipLeft = rect ? Math.min(Math.max(rect.left, 16), window.innerWidth - 336) : window.innerWidth / 2 - 160;

  return (
    <div className="fixed inset-0 z-[95]" data-testid="dashboard-tour" style={{ background: "rgba(20,20,18,0.55)" }}>
      {rect && (
        <div className="absolute rounded-xl pointer-events-none transition-all duration-300"
          data-testid="dashboard-tour-highlight"
          style={{
            top: rect.top - 8, left: rect.left - 8, width: rect.width + 16, height: rect.height + 16,
            boxShadow: "0 0 0 4px var(--brand), 0 0 0 9999px rgba(20,20,18,0.55)",
          }} />
      )}
      <div className="absolute card-soft p-6 w-[320px] transition-all duration-300"
        style={{ top: tooltipTop, left: tooltipLeft }}>
        <div className="text-xs mb-1" style={{ color: "var(--text-secondary)" }}>
          Step {i + 1} of {steps.length}
        </div>
        <div className="font-serif-display text-lg mb-2">{step.title}</div>
        <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>{step.text}</p>
        <div className="flex items-center justify-between gap-2">
          <button data-testid="tour-skip" onClick={() => onDone()} className="text-xs underline"
            style={{ color: "var(--text-secondary)" }}>
            Skip
          </button>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button data-testid="tour-back" onClick={() => setI(i - 1)} className="btn-outline text-xs px-3 py-1.5">
                Back
              </button>
            )}
            <button data-testid={isLast ? "tour-finish" : "tour-next"}
              onClick={() => (isLast ? onDone() : setI(i + 1))}
              className="btn-primary text-xs px-3 py-1.5">
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
